<template>
  <div v-if="status === 'fetching'" class="text-center">
    <b-spinner variant="yellow" />
  </div>
  <div v-else>
    <b-card
      header-bg-variant="yellow"
      header-text-variant="white"
      class="shadow mb-3 mt-3"
    >
      <template #header>
        <span class="float-right text-white">
          <strong>(VIN) {{ $route.params.vin }} </strong>
        </span>
      </template>

      <!-- Row 1 -->
      <div class="grid">
        <div class="nested-grid">
          <div>
            <small>
              <strong>Year</strong><span class="text-danger"> *</span>
            </small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="$v.vehicle.vinData.year.$model"
                class="text-center"
                type="text"
                :value="vehicle.vinData.year"
                :state="validateState('vinData', 'year')"
                @change="setValue('vinData', 'year', $event)"
              />
              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.vinData.year.required">
                  This field is required
                </div>
                <div
                  v-if="
                    !$v.vehicle.vinData.year.minValue ||
                    !$v.vehicle.vinData.year.maxValue
                  "
                >
                  Invalid value
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>
        <!-- Make -->
        <div class="nested-grid">
          <div>
            <small>
              <strong>Make</strong>
              <span class="text-danger"> *</span>
            </small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="$v.vehicle.vinData.make.$model"
                class="text-center"
                type="text"
                :value="vehicle.vinData.make"
                :state="validateState('vinData', 'make')"
                @change="setValue('vinData', 'make', $event)"
              />
              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.vinData.make.required">
                  This field is required
                </div>
                <div
                  v-if="
                    !$v.vehicle.vinData.make.maxLength ||
                    !$v.vehicle.vinData.make.minLength
                  "
                >
                  Invalid value
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>
        <!-- Model -->
        <div class="nested-grid">
          <div>
            <small>
              <strong>Model</strong>
              <span class="text-danger"> *</span>
            </small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="$v.vehicle.vinData.model.$model"
                class="text-center"
                type="text"
                :value="vehicle.vinData.model"
                :state="validateState('vinData', 'model')"
                @change="setValue('vinData', 'model', $event)"
              />
              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.vinData.model.required">
                  This field is required
                </div>
                <div
                  v-if="
                    !$v.vehicle.vinData.model.maxLength ||
                    !$v.vehicle.vinData.model.minLength
                  "
                >
                  Invalid value
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>
        <!-- Trim -->
        <div class="nested-grid">
          <div>
            <small><strong>Trim</strong></small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="vehicle.vinData.trim"
                class="text-center"
                type="text"
                :value="vehicle.vinData.trim"
              />
            </b-form-group>
          </div>
        </div>
      </div>

      <!-- Row 2 -->
      <div class="grid">
        <!-- Options -->
        <div class="nested-grid">
          <div>
            <small>
              <strong>Options</strong>
            </small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="vehicle.vinData.options"
                class="text-center"
                type="text"
                :value="vehicle.vinData.options"
              />
            </b-form-group>
          </div>
        </div>
        <!-- Style -->
        <div class="nested-grid">
          <div>
            <small><strong>Style</strong></small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="vehicle.vinData.style"
                class="text-center"
                type="text"
                :value="vehicle.vinData.style"
              />
            </b-form-group>
          </div>
        </div>
        <!--Doors -->
        <div class="nested-grid">
          <div>
            <small><strong>Doors</strong></small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="vehicle.vinData.doors"
                class="text-center"
                type="number"
                :value="vehicle.vinData.doors"
              />
            </b-form-group>
          </div>
        </div>
        <!-- Vehicle type -->
        <div class="nested-grid">
          <div>
            <small><strong>Vehicle Type</strong></small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="vehicle.vinData.vehicleType"
                class="text-center"
                type="text"
                :value="vehicle.vinData.vehicleType"
              />
            </b-form-group>
          </div>
        </div>
      </div>

      <!-- Row 3 -->
      <div class="grid">
        <!-- Drive type -->
        <div class="nested-grid">
          <div>
            <small><strong>Drive Type</strong></small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="vehicle.vinData.driveType"
                class="text-center"
                type="text"
                :value="vehicle.vinData.driveType"
              />
            </b-form-group>
          </div>
        </div>

        <!-- Engine -->
        <div class="nested-grid">
          <div>
            <small><strong>Engine</strong></small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="vehicle.vinData.engine"
                class="text-center"
                type="text"
                :value="vehicle.vinData.engine"
              />
            </b-form-group>
          </div>
        </div>

        <!-- Transmission -->
        <div class="nested-grid">
          <div>
            <small><strong>Transmission</strong></small>
          </div>
          <div>
            <b-input-group v-if="!disabled" class="d-flex justify-content-end">
              <b-form-radio-group
                v-model.trim="vehicle.vinData.transmissionType"
                style="max-height: 2.3rem"
                class="transmission"
                :value="vehicle.vinData.transmissionType"
                :options="typeOptions"
                name="transmission"
                button-variant="outline-primary"
                buttons
              />
            </b-input-group>
            <b-form-group v-else>
              <b-form-input
                class="text-center"
                :disabled="disabled"
                type="text"
                :value="car.transmissionType"
              />
            </b-form-group>
          </div>
        </div>

        <!-- Color -->
        <div class="nested-grid">
          <div>
            <small>
              <strong>Color</strong>
              <span class="text-danger"> *</span>
            </small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="$v.vehicle.vehicleInformation.color.$model"
                class="text-center"
                :disabled="disabled"
                :value="vehicle.vehicleInformation.color"
                :state="validateState('vehicleInformation', 'color')"
                type="text"
                @change="setValue('vehicleInformation', 'color', $event)"
              />
              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.vehicleInformation.color.required">
                  This field is required
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>
      </div>

      <!-- Row 4 -->
      <div class="grid">
        <!-- Miles -->
        <div class="nested-grid">
          <div>
            <small>
              <strong>Miles</strong>
              <span class="text-danger"> *</span>
            </small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="$v.vehicle.vehicleInformation.miles.$model"
                class="text-center"
                :disabled="disabled"
                :value="vehicle.vehicleInformation.miles"
                :state="validateState('vehicleInformation', 'miles')"
                type="number"
                @change="setValue('vehicleInformation', 'miles', $event)"
              />
              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.vehicleInformation.miles.required">
                  This field is required
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>

        <!-- Cost -->
        <div class="nested-grid">
          <div>
            <small>
              <strong>Cost</strong>
              <span class="text-danger"> *</span>
            </small>
          </div>
          <div>
            <b-form-group>
              <Money
                v-model.trim="$v.vehicle.vehicleCostDetail.cost.$model"
                :disabled="userIsAgent"
                style="background-color: #35a4dd"
                class="form-control text-center text-white"
                v-bind="money"
                :state="validateState('vehicleCostDetail', 'cost')"
                @change="setValue('vehicleCostDetail', 'cost', $event)"
              />
              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.vehicleCostDetail.cost.required">
                  This field is required
                </div>
                <div
                  v-if="
                    !$v.vehicle.vehicleCostDetail.cost.maxLength ||
                    !$v.vehicle.vehicleCostDetail.cost.minLength
                  "
                >
                  Invalid value
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>

        <!-- Price -->
        <div class="nested-grid">
          <div>
            <small>
              <strong>Price <span class="text-danger"> *</span> </strong>
            </small>
          </div>
          <div>
            <b-form-group>
              <Money
                v-model.trim="$v.vehicle.vehicleCostDetail.price.$model"
                style="background-color: #35a4dd"
                class="form-control text-center text-white"
                v-bind="money"
                :state="validateState('vehicleCostDetail', 'price')"
                @change="setValue('vehicleCostDetail', 'price', $event)"
              />
              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.vehicleCostDetail.price.required">
                  This field is required
                </div>
                <div
                  v-if="
                    !$v.vehicle.vehicleCostDetail.price.maxLength ||
                    !$v.vehicle.vehicleCostDetail.price.minLength
                  "
                >
                  Invalid value
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>

        <!-- Stock -->
        <div class="nested-grid">
          <div>
            <small>
              <strong>Stock</strong>
              <span class="text-danger"> *</span>
            </small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="$v.vehicle.stockNumber.$model"
                class="text-center"
                :state="validateState(null, 'stockNumber')"
                @change="setValue(null, 'stockNumber', $event)"
              />
              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.stockNumber.required">
                  This field is required
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>
      </div>

      <!-- Row 5 -->
      <div class="grid">
        <!-- Release Date -->
        <div class="nested-grid">
          <div>
            <small><strong>Release Date</strong></small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="vehicle.releaseDate"
                class="text-center"
                type="date"
              />
            </b-form-group>
          </div>
        </div>

        <!-- CrUrL -->
        <div class="nested-grid">
          <div>
            <small>
              <strong>CrUrl</strong>
              <span class="text-danger"> *</span>
            </small>
          </div>
          <div>
            <b-form-group>
              <b-form-input
                v-model.trim="$v.vehicle.crUrl.$model"
                class="text-center"
                :value="vehicle.crUrl"
                :state="validateState(null, 'crUrl')"
                type="text"
                @change="setValue(null, 'crUrl', $event)"
              />
              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.crUrl.required">
                  This field is required
                </div>
                <div
                  v-if="
                    !$v.vehicle.crUrl.maxLength || !$v.vehicle.crUrl.minLength
                  "
                >
                  Invalid value
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>

        <!-- Agent -->
        <div v-if="usersNicknames" class="nested-grid">
          <div>
            <small>
              <strong>Agent</strong>
              <span class="text-danger"> *</span>
            </small>
          </div>
          <div>
            <b-form-group>
              <b-form-select
                v-model="vehicle.userId"
                :options="usersNicknames.data"
                value-field="id"
                text-field="nickname"
                class="agent"
                :state="validateState(null, 'userId')"
                @change="setValue(null, 'userId', $event)"
              >
                <template v-slot:first>
                  <b-form-select-option :value="null">
                    Select
                  </b-form-select-option>
                </template>
              </b-form-select>
              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.userId.required">
                  This field is required
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>

        <!-- Location -->
        <div class="nested-grid">
          <div>
            <small>
              <strong>Location</strong>
              <span class="text-danger"> *</span>
            </small>
          </div>
          <div>
            <b-form-group>
              <b-form-select
                v-model.trim="$v.vehicle.location.$model"
                :value="vehicle.location"
                :options="locations"
                class="location"
                :state="validateState(null, 'location')"
                @change="setValue(null, 'location', $event)"
              >
                <template v-slot:first>
                  <b-form-select-option :value="null">
                    Select
                  </b-form-select-option>
                </template>
              </b-form-select>

              <b-form-invalid-feedback>
                <div v-if="!$v.vehicle.location.required">
                  This field is required
                </div>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>
      </div>
    </b-card>
    <div v-if="car">
      <UploadPhotos
        :vehicle-id="car.id"
        :vehicle-has-parts-props="car.vehicleHasParts"
        :vehicle-external-images-props="car.vehicleExternalImages"
      />
    </div>
    <b-container fluid class="mt-4">
      <b-row>
        <b-col sm="2" xl="6" class="d-flex">
          <b-button
            variant="danger"
            size="lg"
            class="trash-button"
            @click="deleteVehicle()"
          >
            <b-icon icon="trash-fill" />
          </b-button>
        </b-col>
        <b-col sm="2" xl="6" class="d-flex justify-content-end">
          <div class="cancel-next-button">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              size="lg"
              @click="cancelEdit()"
            >
              Cancel
            </b-button>
            <b-button
              size="lg"
              type="submit"
              variant="yellow"
              :disabled="$v.vehicle.$invalid"
              @click="nextPage"
            >
              Next
              <b-icon icon="chevron-compact-right" scale="1" />
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { Money } from "v-money";
import { registryVehicleMixin } from "./mixins";
import { generalMixin } from "@/modules/mixin";
import {
  required,
  maxValue,
  minValue,
  maxLength,
  minLength,
} from "vuelidate/lib/validators";
import UploadPhotos from "./uploadPhotos/UploadPhotos";
export default {
  components: { Money, UploadPhotos },
  mixins: [registryVehicleMixin, generalMixin],
  props: ["carProp", "disabled", "reservedPriceProp"],
  data() {
    return {
      money: {
        decimal: ".",
        thousands: ",",
        prefix: "CAD$ ",
        suffix: "",
        precision: 2,
        masked: false /* doesn't work with directive */,
      },
      typeOptions: [
        { value: "Manual", text: "M" },
        { value: "Automatic", text: "A" },
        { value: "CVT", text: "CVT" },
      ],
      locations: [
        { value: "MICHIGAN", text: "MICHIGAN" },
        { value: "NEW YORK", text: "NEW YORK" },
      ],
    };
  },
  validations: {
    vehicle: {
      stockNumber: {
        required,
      },
      crUrl: {
        required,
        maxLength: maxLength(255),
        minLength: minLength(5),
      },
      userId: {
        required,
      },
      location: {
        required,
      },
      vehicleCostDetail: {
        cost: {
          required,
          maxValue: maxValue(1000000),
          minValue: minValue(1),
        },
        price: {
          required,
          maxValue: maxValue(1000000),
          minValue: minValue(1),
        },
      },
      vehicleInformation: {
        color: {
          required,
        },
        miles: {
          required,
          maxValue: maxValue(1000000),
          minValue: minValue(1),
        },
      },
      vinData: {
        year: {
          required,
          maxValue: maxValue(2050),
          minValue: minValue(1900),
        },
        make: {
          required,
          maxLength: maxLength(50),
          minLength: minLength(2),
        },
        model: {
          required,
          maxLength: maxLength(50),
          minLength: minLength(2),
        },
      },
    },
  },
  beforeMount() {
    this.clearVehicleToEdit();
    this.getVehicleByVin();
    this.getUserNickNames();
  },
  methods: {
    setValue(key, attribute, value) {
      if (key) {
        this.vehicle[key][attribute] = value;
        this.$v.vehicle[key][attribute].$touch();
      } else {
        this.vehicle[attribute] = value;
        this.$v.vehicle[attribute].$touch();
      }
    },
    validateState(key, name) {
      const { $dirty, $error } = key
        ? this.$v.vehicle[key][name]
        : this.$v.vehicle[name];
      return $dirty ? !$error : null;
    },
    confirmDeleteVehicle() {
      return this.$bvModal.msgBoxConfirm(
        "Please confirm that you want to delete the vehicle.",
        {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        }
      );
    },
    async deleteVehicle() {
      const confirm = await this.confirmDeleteVehicle();
      if (!confirm) {
        return;
      } else {
        this.updateStatus("Inactive");
      }
    },
    cancelEdit() {
      this.$router.push({
        name: this.userIsAdmin ? "admin.vehicles.list" : "agent.vehicles.list",
      });
    },
  },
};
</script>

<style scoped>
.border-left {
  border-left: 5px black;
}
.bg-primary {
  background-color: #35a4dd;
}
.font-small {
  font-size: 12px;
}
.border-b {
  border-bottom: 10px black;
}
.transmission {
  width: 100%;
}

.grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1em;
}

.nested-grid {
  display: grid;
  grid-template-columns: 30% 70%;
}

@media (max-width: 576px) {
  .grid {
    grid-template-columns: repeat(1, 1fr);
  }

  .cancel-next-button {
    margin-top: -45px;
  }
}

@media (max-width: 300px) {
  .nested-grid {
    grid-template-columns: repeat(1, 1fr);
  }
  .trash-button {
    position: absolute;
    margin-top: -15px;
    margin-left: 10px;
  }
  .cancel-next-button {
    margin-top: 40px;
  }
}
</style>
